<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_from_category,
		real_id,
		get_device_component_index,
		get_gen_2_component_channel_structure,
	} from '../../../../helpers/interface_helpers';
	//Stores
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Button, Input, toast, Text, Alert } from '@Shelly/shelly-design-system';
	import { makeRangePrimaryAndBold } from 'src/helpers/utilities';
	import { restrictDecimalNumberInput } from 'src/helpers/number_input_helpers';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device;

	const unit = 'V';
	const deviceType = get_gen_2_component_from_category(device);
	const index = get_device_component_index(device);

	let id = device.id;
	let settings = readDeviceSettings(id);

	// default values of the range
	const MIN_VOLTAGE_LIMIT = 0;
	const MAX_VOLTAGE_LIMIT = 280;

	let settingsUndervoltageLimit =
		settings?.[get_gen_2_component_channel_structure(device)]?.undervoltage_limit;
	let settingsOvervoltageLimit =
		settings?.[get_gen_2_component_channel_structure(device)]?.voltage_limit;

	let loading = false;

	//UNDER VOLTAGE
	let undervoltageValue = settingsUndervoltageLimit ?? MIN_VOLTAGE_LIMIT;
	let undervoltageError = '';

	//OVER VOLTAGE
	let overvoltageValue = settingsOvervoltageLimit ?? MAX_VOLTAGE_LIMIT;
	let overvoltageError = '';

	/**
	 * Calculates the maximum allowed value for undervoltage protection based on the overvoltage value
	 * @param {number} overvoltageVal - The current overvoltage protection value
	 * @returns {number} The maximum allowed undervoltage value, which is either:
	 *                   - MAX_VOLTAGE_LIMIT - 1 if overvoltageVal is undefined, 0, or exceeds MAX_VOLTAGE_LIMIT - 1
	 *                   - overvoltageVal - 1 otherwise
	 * @description Ensures that the undervoltage value is always at least 1V less than the overvoltage value
	 *              to maintain a safe voltage range and prevent conflicts in voltage protection settings
	 */
	const calculateUndervoltageMAXLimit = (overvoltageVal: number) => {
		// If overvoltage value is undefined (empty string), use the default value
		if (
			overvoltageVal === undefined ||
			overvoltageVal === 0 ||
			overvoltageVal > MAX_VOLTAGE_LIMIT - 1
		) {
			return MAX_VOLTAGE_LIMIT - 1;
		}

		return overvoltageVal - 1;
	};

	/**
	 * Calculates the minimum limit for overvoltage protection based on the undervoltage value
	 *
	 * @param {number} undervoltageVal - The undervoltage protection value
	 * @returns {number} The minimum overvoltage protection limit
	 *
	 * @description
	 * This function ensures there is at least 1V difference between undervoltage and overvoltage protection values.
	 * If the undervoltage value is within valid range (0 < value < MAX_VOLTAGE_LIMIT),
	 * returns undervoltage + 1V as minimum overvoltage limit.
	 * Otherwise returns the minimum voltage limit constant.
	 */
	const calculateOvervoltageMINLimit = (undervoltageVal: number) => {
		if (undervoltageVal < MAX_VOLTAGE_LIMIT && undervoltageVal > 0) {
			return undervoltageVal + 1;
		}

		return MIN_VOLTAGE_LIMIT;
	};

	/**
	 * Voltage protection range limits calculation .
	 *
	 * @typedef {Object} VoltageProtectionLimits
	 * @property {number} undervoltageMAXLimit - Maximum allowed value for undervoltage setting.
	 *                                          Cannot exceed overvoltage value.
	 * @property {number} overvoltageMINLimit - Minimum allowed value for overvoltage setting.
	 *                                          Cannot be less than undervoltage value.
	 *
	 * These limits are:
	 * 1. Used to constrain input field values
	 * 2. Displayed as informational messages to guide users
	 * 3. Dynamically recalculated when either voltage value changes
	 */
	let undervoltageMAXLimit = calculateUndervoltageMAXLimit(overvoltageValue);
	let overvoltageMINLimit = calculateOvervoltageMINLimit(undervoltageValue);

	/**
	 * Handles validation errors for voltage range protection settings
	 * Validates whether the input values are within acceptable voltage ranges
	 * Updates error states and messages if validation fails
	 * @returns {void}
	 */
	const handleErrors = () => {
		// Clear errors by default
		undervoltageError = '';
		overvoltageError = '';

		// Check undervoltage constraints
		/**
		 * Validates the undervoltage protection value against defined limits and overvoltage value
		 *
		 * @description Checks if:
		 * 1. The undervoltage value is below the minimum allowed voltage limit
		 * 2. Both undervoltage and overvoltage values exceed the maximum undervoltage limit
		 * 3. The undervoltage value exceeds the maximum allowed voltage limit
		 * @param {number} undervoltageValue - The undervoltage protection value to validate
		 * @param {number} undervoltageMAXLimit - The maximum allowed undervoltage limit
		 * @param {number} overvoltageValue - The current overvoltage protection value
		 * @param {number} MIN_VOLTAGE_LIMIT - The minimum allowed voltage limit
		 * @param {number} MAX_VOLTAGE_LIMIT - The maximum allowed voltage limit
		 * @returns {boolean} True if any of the conditions are met, indicating invalid value
		 * These conditions ensure the voltage protection settings remain within safe operating ranges
		 * and maintain proper relationship between under and overvoltage thresholds
		 */
		if (
			undervoltageValue < MIN_VOLTAGE_LIMIT ||
			(undervoltageValue > undervoltageMAXLimit && overvoltageValue > undervoltageMAXLimit) ||
			undervoltageValue > MAX_VOLTAGE_LIMIT
		) {
			undervoltageError = $format('error_out_of_range', {
				default: `Value ${undervoltageValue} ${unit} is out of range. Please provide a value within the limits. (${MIN_VOLTAGE_LIMIT} ${unit} — ${undervoltageMAXLimit} ${unit})`,
				values: {
					value: undervoltageValue,
					min: MIN_VOLTAGE_LIMIT,
					max: undervoltageMAXLimit,
					unit,
				},
			});
		}

		// Check overvoltage constraints
		/**
		 * Validates the overvoltage protection value against defined limits
		 * @description Checks if:
		 * 1. The overvoltage value is 0
		 * 2. The value is below the minimum overvoltage limit
		 * 3. The value exceeds the maximum voltage limit
		 * @param {number} overvoltageValue - The overvoltage protection value to validate
		 * @param {number} overvoltageMINLimit - The minimum allowed overvoltage limit
		 * @param {number} MAX_VOLTAGE_LIMIT - The default maximum allowed voltage limit
		 * @returns {boolean} True if any of the conditions are met, indicating invalid value
		 */
		if (
			overvoltageValue === 0 ||
			overvoltageValue < overvoltageMINLimit ||
			overvoltageValue > MAX_VOLTAGE_LIMIT
		) {
			overvoltageError = $format('error_out_of_range', {
				default: `Value ${overvoltageValue} ${unit} is out of range. Please provide a value within the limits. (${overvoltageMINLimit} ${unit} — ${MAX_VOLTAGE_LIMIT} ${unit})`,
				values: {
					value: overvoltageValue,
					min: overvoltageMINLimit,
					max: MAX_VOLTAGE_LIMIT,
					unit,
				},
			});
		}
	};

	const setVoltageProtection = async () => {
		overvoltageValue = isNaN(overvoltageValue) ? MAX_VOLTAGE_LIMIT : overvoltageValue;

		undervoltageValue = isNaN(undervoltageValue) ? MIN_VOLTAGE_LIMIT : undervoltageValue;

		const params = {
			id: index,
			config: {
				voltage_limit: overvoltageValue,
				undervoltage_limit: undervoltageValue,
			},
		};
		loading = true;

		const res: SettingRequest = await gen2Request(device, `${deviceType}.setconfig`, params);

		if (res.isok) {
			settings[`${deviceType}:${index}`].voltage_limit = overvoltageValue;
			settings[`${deviceType}:${index}`].undervoltage_limit = undervoltageValue;
			devices_settings.update_devices_settings(id, settings);

			toast.open({
				type: 'success',
				title: $format('max_voltage_prot_save_scc_toast', {
					default: 'Successfully saved voltage protection value.',
				}),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('max_voltage_prot_save_err_toast'),
			});
		}
		loading = false;
	};

	//This variable is used to display the range of the voltage protection in bold and primary color
	$: valueUndervoltage_valueOvervoltage = `${
		!isNaN(undervoltageValue) ? undervoltageValue : 0
	} - ${!isNaN(overvoltageValue) ? overvoltageValue : 280}`;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: setVoltageProtection,
				loading,
				disabled: loading || !!overvoltageError || !!undervoltageError,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Alert
	type="info"
	class="mb-s"
	heading={$format('delete_to_reset_default_protection_value', {
		default: `If you delete a value and save, the following default values will be applied:`,
	})}
	message={`
		<ul class="mt-0 mb-0">
			<li>
				${$format('under_voltage_0_v', {
					default: 'Under voltage:<strong> 0 V</strong>',
				})}
			</li>
			<li>
				${$format('over_voltage_280_v', {
					default: 'Over voltage:<strong> 280 V</strong>',
				})}
			</li>
		</ul>`}
/>
<Text styleType="controls-small">
	{@html makeRangePrimaryAndBold(
		`${$format('voltage_prot_description', {
			values: { value: valueUndervoltage_valueOvervoltage },
			default: `Configure Shelly device to turn off when voltage exceeds the ${valueUndervoltage_valueOvervoltage} V range`,
		})}`,
	)}
</Text>

<!--#region UNDERVOLTAGE PROTECTION-->
<Text styleType="controls-small" class="mt-m mb-s">
	{$format('under_voltage_protection', {
		default: `Under voltage protection`,
	})}
</Text>

<Input
	on:input={(e) => {
		restrictDecimalNumberInput(e, 2);
		undervoltageValue = e.target['value'] === '' ? undefined : Number(e.target['value']);
		overvoltageMINLimit = calculateOvervoltageMINLimit(undervoltageValue);
		handleErrors();
	}}
	value={undervoltageValue}
	size="m"
	class="mb-s"
	type={'number'}
	min={MIN_VOLTAGE_LIMIT}
	max={undervoltageMAXLimit}
	info={$format('min_max_error_description', {
		values: {
			min: MIN_VOLTAGE_LIMIT,
			max: undervoltageMAXLimit < 0 ? MAX_VOLTAGE_LIMIT - 1 : undervoltageMAXLimit,
		},
		default: `Value must be between ${MIN_VOLTAGE_LIMIT} and ${
			undervoltageMAXLimit < 0 ? MAX_VOLTAGE_LIMIT - 1 : undervoltageMAXLimit
		}.`,
	})}
	error={undervoltageError}
	step={1}
	on:enter={setVoltageProtection}
	id="input-qm9unb6"
	aria-label={$format('under_voltage_protection', {
		default: `Under voltage protection`,
	})}
/>
<!-- #end region UNDERVOLTAGE PROTECTION -->
<!--#region OVERVOLTAGE PROTECTION-->
<Text styleType="controls-small" class="mt-s mb-s">
	{$format('over_voltage_protection', {
		default: `Over voltage protection`,
	})}
</Text>

<Input
	on:input={(e) => {
		restrictDecimalNumberInput(e, 2);
		overvoltageValue = e.target['value'] === '' ? undefined : Number(e.target['value']);
		undervoltageMAXLimit = calculateUndervoltageMAXLimit(overvoltageValue);
		handleErrors();
	}}
	value={overvoltageValue}
	class="mb-s"
	size="m"
	type={'number'}
	min={undervoltageValue}
	max={MAX_VOLTAGE_LIMIT}
	info={$format('min_max_error_description', {
		values: {
			min: overvoltageMINLimit,
			max: MAX_VOLTAGE_LIMIT,
		},
		default: `Value must be between ${overvoltageMINLimit} and ${MAX_VOLTAGE_LIMIT}.`,
	})}
	error={overvoltageError}
	step={1}
	on:enter={setVoltageProtection}
	id="input-zte8h5m"
	aria-label={$format('over_voltage_protection', {
		default: `Over voltage protection`,
	})}
/>
<!--#endregion OVERVOLTAGE PROTECTION-->

{#if !$showNewLayout}
	<Button
		class="mt-s"
		{loading}
		disabled={loading || overvoltageError || undervoltageError}
		size="m"
		width="full"
		on:click={setVoltageProtection}
	>
		{$format('button_save')}
	</Button>
{/if}
